import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Phone, Mail, MessageSquare, RefreshCw, Play } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('cia_admin_token')}`,
  'Content-Type': 'application/json',
});

export default function AdminDiagnostics() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [testPhone, setTestPhone] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/customers`, { headers: getHeaders() });
      const data = await res.json();
      setCustomers(data.customers || []);
    } catch (e) {}
  };

  const log = (msg, type = 'info') => {
    setLogs(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
  };

  const runTest = async (testName, fn) => {
    setLoading(prev => ({ ...prev, [testName]: true }));
    setResults(prev => ({ ...prev, [testName]: null }));
    log(`Running: ${testName}...`);
    try {
      const result = await fn();
      setResults(prev => ({ ...prev, [testName]: result }));
      log(`${testName}: ${result.success ? '✅ Success' : '❌ Failed — ' + result.error}`, result.success ? 'success' : 'error');
    } catch (e) {
      setResults(prev => ({ ...prev, [testName]: { success: false, error: e.message } }));
      log(`${testName}: ❌ Error — ${e.message}`, 'error');
    }
    setLoading(prev => ({ ...prev, [testName]: false }));
  };

  const testSMS = () => runTest('sms', async () => {
    const phone = testPhone || selectedCustomer?.phone;
    if (!phone) return { success: false, error: 'No phone number' };
    const res = await fetch(`${API_BASE}/admin/test-sms`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ phone, message: '[Aizaan Diagnostic] SMS test — if you receive this, SMS delivery is working ✅' }),
    });
    return res.json();
  });

  const testEmail = () => runTest('email', async () => {
    const email = emailInput || selectedCustomer?.email;
    if (!email) return { success: false, error: 'No email address' };
    const res = await fetch(`${API_BASE}/admin/test-email`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email, subject: 'Aizaan Diagnostic — Email Test', message: 'If you receive this email, email delivery is working correctly.' }),
    });
    return res.json();
  });

  const testCall = () => runTest('call', async () => {
    const phone = testPhone || selectedCustomer?.phone;
    if (!phone) return { success: false, error: 'No phone number' };
    const res = await fetch(`${API_BASE}/admin/test-call`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ phone }),
    });
    return res.json();
  });

  const testFullPipeline = () => runTest('pipeline', async () => {
    if (!selectedCustomer) return { success: false, error: 'Select a customer first' };
    const res = await fetch(`${API_BASE}/admin/test-pipeline`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ customer_id: selectedCustomer.id }),
    });
    return res.json();
  });

  const checkNumberStatus = () => runTest('numbers', async () => {
    const res = await fetch(`${API_BASE}/admin/check-numbers`, { headers: getHeaders() });
    return res.json();
  });

  const extendTrial = (customerId, days = 14) => runTest(`extend_${customerId}`, async () => {
    const res = await fetch(`${API_BASE}/admin/extend-trial`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ customer_id: customerId, days }),
    });
    return res.json();
  });

  const fixNumberProfiles = () => runTest('fix_profiles', async () => {
    const res = await fetch(`${API_BASE}/admin/fix-number-profiles`, {
      method: 'POST',
      headers: getHeaders(),
    });
    return res.json();
  });

  const StatusBadge = ({ result, name }) => {
    if (loading[name]) return <span style={{ fontSize: 12, color: '#f59e0b' }}>Running...</span>;
    if (!result) return null;
    if (result.success) return <CheckCircle size={16} color="#00C896" />;
    return <XCircle size={16} color="#ef4444" />;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, fontFamily: 'monospace' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Admin Diagnostics</h1>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
        Test SMS, email, calls, and number configuration without depending on US users
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>

        {/* Customer selector */}
        <div>
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Select Customer</h3>
            {customers.map(c => (
              <div
                key={c.id}
                onClick={() => {
                  setSelectedCustomer(c);
                  setTestPhone(c.phone || '');
                  setEmailInput(c.email || '');
                }}
                style={{
                  padding: '8px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                  background: selectedCustomer?.id === c.id ? 'var(--bg-hover)' : 'transparent',
                  border: selectedCustomer?.id === c.id ? '1px solid var(--blue)' : '1px solid transparent',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500 }}>{c.business_name || c.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.phone}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</div>
                <div style={{ fontSize: 11, color: c.is_active ? '#00C896' : '#ef4444' }}>
                  {c.plan || 'trial'} · {c.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            ))}
          </div>

          {/* Extend Trial */}
          {selectedCustomer && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Extend Trial</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                {selectedCustomer.business_name || selectedCustomer.name}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[7, 14, 30].map(days => (
                  <button
                    key={days}
                    onClick={() => extendTrial(selectedCustomer.id, days)}
                    disabled={loading[`extend_${selectedCustomer.id}`]}
                    className="ob-btn ob-btn-ghost"
                    style={{ flex: 1, fontSize: 12, padding: '6px 8px' }}
                  >
                    +{days} days
                  </button>
                ))}
              </div>
              {results[`extend_${selectedCustomer.id}`] && (
                <div style={{
                  marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6,
                  background: results[`extend_${selectedCustomer.id}`].success ? 'rgba(0,200,150,0.1)' : 'rgba(239,68,68,0.1)',
                  color: results[`extend_${selectedCustomer.id}`].success ? '#00C896' : '#ef4444',
                }}>
                  {results[`extend_${selectedCustomer.id}`].message || results[`extend_${selectedCustomer.id}`].error}
                </div>
              )}
            </div>
          )}

          {/* Manual override */}
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Manual Override</h3>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Phone</label>
              <input
                className="ob-input"
                style={{ fontSize: 13, padding: '6px 10px' }}
                placeholder="+1XXXXXXXXXX"
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Email</label>
              <input
                className="ob-input"
                style={{ fontSize: 13, padding: '6px 10px' }}
                placeholder="test@example.com"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Test buttons */}
        <div>
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>Notification Tests</h3>

            {[
              { name: 'sms', label: 'Send Test SMS', icon: MessageSquare, fn: testSMS, desc: 'Sends a test SMS to the selected phone number' },
              { name: 'email', label: 'Send Test Email', icon: Mail, fn: testEmail, desc: 'Sends a test email to the selected email address' },
              { name: 'call', label: 'Make Test Call', icon: Phone, fn: testCall, desc: 'Places a test call to the selected phone number' },
              { name: 'pipeline', label: 'Test Full Pipeline', icon: Play, fn: testFullPipeline, desc: 'Simulates a complete call capture + notification flow for selected customer' },
            ].map(({ name, label, icon: Icon, fn, desc }) => (
              <div key={name} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0', borderBottom: '1px solid var(--border)',
              }}>
                <Icon size={16} color="var(--blue)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{desc}</div>
                  {results[name] && !results[name].success && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
                      Error: {results[name].error}
                    </div>
                  )}
                  {results[name]?.success && results[name]?.details && (
                    <div style={{ fontSize: 11, color: '#00C896', marginTop: 4 }}>
                      {JSON.stringify(results[name].details)}
                    </div>
                  )}
                </div>
                <StatusBadge result={results[name]} name={name} />
                <button
                  onClick={fn}
                  disabled={loading[name]}
                  className="ob-btn ob-btn-primary"
                  style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
                >
                  {loading[name] ? 'Running...' : 'Run'}
                </button>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>Number Health</h3>
            {[
              { name: 'numbers', label: 'Check All Numbers', icon: Phone, fn: checkNumberStatus, desc: 'Verify all provisioned numbers have correct messaging profile + call control' },
              { name: 'fix_profiles', label: 'Fix Missing Profiles', icon: RefreshCw, fn: fixNumberProfiles, desc: 'Auto-assign Aizaan-Messaging profile to any number missing it' },
            { name: 'fix_durations', label: 'Fix Call Durations', icon: RefreshCw, fn: () => runTest('fix_durations', async () => {
              const res = await fetch(`${API_BASE}/admin/fix-call-durations`, { method: 'POST', headers: getHeaders() });
              return res.json();
            }), desc: 'Backfill duration from Telnyx for calls showing 0 seconds' },
            ].map(({ name, label, icon: Icon, fn, desc }) => (
              <div key={name} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0', borderBottom: '1px solid var(--border)',
              }}>
                <Icon size={16} color="var(--orange)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{desc}</div>
                  {results[name]?.success && (
                    <div style={{ fontSize: 11, color: '#00C896', marginTop: 4 }}>
                      {results[name].message || (Array.isArray(results[name].fixed) ? `Fixed: ${results[name].fixed.join(', ')}` : `Fixed: ${results[name].fixed}`)}
                    </div>
                  )}
                </div>
                <StatusBadge result={results[name]} name={name} />
                <button
                  onClick={fn}
                  disabled={loading[name]}
                  className="ob-btn ob-btn-ghost"
                  style={{ fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap' }}
                >
                  {loading[name] ? 'Running...' : 'Run'}
                </button>
              </div>
            ))}
          </div>

          {/* Log console */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600 }}>Diagnostic Log</h3>
              <button onClick={() => setLogs([])} style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>Clear</button>
            </div>
            <div style={{
              background: '#0d1117', borderRadius: 6, padding: 12,
              fontFamily: 'monospace', fontSize: 12, maxHeight: 200, overflowY: 'auto',
              minHeight: 80,
            }}>
              {logs.length === 0 ? (
                <span style={{ color: '#4b5563' }}>Run a test to see results here...</span>
              ) : logs.map((l, i) => (
                <div key={i} style={{ color: l.type === 'success' ? '#00C896' : l.type === 'error' ? '#ef4444' : '#9ca3af', marginBottom: 3 }}>
                  <span style={{ color: '#4b5563' }}>[{l.time}]</span> {l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
